import { Config, markdownToHtml } from './helper';
import showdown from 'showdown';
import hljs from 'highlight.js';

export function highlightElement(survey, options) {
  setTimeout(() => {
    options.htmlElement.querySelectorAll('pre code').forEach((block) => {
      hljs.configure({
        languages: ['python']
      });
      hljs.highlightElement(block);
    });

  }, 400);
}

export function onTextMarkdownHandler(survey, options) {
  let converter = new showdown.Converter();
  //convert the markdown text to html
  let str = converter.makeHtml(options.text);
  //remove root paragraphs <p></p> if they're wrapping the entire content
  if (str.startsWith('<p>') && str.endsWith('</p>')) {
    str = str.substring(3, str.length - 4);
  }
  //set html
  options.html = str;
}

export function disableNextButton(sender, options) {
  const formElement = options.htmlElement.closest('.nv-form');
  const nextButton = formElement.querySelector(".sd-navigation__next-btn");
  const hasExplanationTitle = formElement.querySelector('.explanation-title');

  const customIndex = Math.floor(sender.currentPageNo / 2) + 1;
  options.htmlElement.querySelectorAll('.sd-element__num').forEach(el => {
    el.innerHTML = `${customIndex}. `;
  });

  if (hasExplanationTitle) {
    return;
  }

  if (!nextButton) return;
  nextButton.disabled = true;
}

export function enableNextButton(sender, options) {
  const currentPage = sender.currentPage;

  const nextButton = document.querySelector(".sd-navigation__next-btn");
  if (!currentPage || !nextButton) return;

  const allAnswered = currentPage.questions.every(
    q => !q.isRequired || q.value !== null
  );

  nextButton.disabled = !allAnswered;
}

export function pageHandler(sender, options) {
  const index = sender.currentPageNo; // 0-based page index

  if ((index + 1) % 2 === 1) {
    sender.pageNextText = Config.I18n.Quiz.RevealAnswer;

    setTimeout(() => {
      const container = document.querySelector('.sd-body__page');
      const target = container?.querySelector('.sd-question');

      if (container && target) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const offset = targetRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({
          top: offset,
          behavior: 'smooth',
        });
      }
    }, 300);

  } else {
    sender.pageNextText = Config.I18n.Quiz.NextQuestion;
    const currentPage = sender.currentPage;
    const prevPage = sender.getPage(index - 1);
    if (!prevPage) return;

    prevPage.questions.forEach(q => {
      const userAnswer = sender.getValue(q.name);
      const correctAnswer = q.correctAnswer;

      let isCorrect = false;
      let correctLabels = [];

      if (Array.isArray(correctAnswer)) {
        correctLabels = correctAnswer
          .map(val => {
            const choice = q.choices.find(c => c.value === val);
            return choice ? choice.text : val;
          });
      } else {
        const choice = q.choices?.find(c => c.value === correctAnswer);
        correctLabels = choice ? [choice.text] : [correctAnswer];
      }

      let correctAnswerIndex = null;
      let userAnswerIndex = null;
      switch (q.getType()) {
        case 'checkbox':
          if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
            isCorrect = userAnswer.length === correctAnswer.length && userAnswer.every(a => correctAnswer.includes(a));
          }
          break;
        case 'radiogroup':
        case 'dropdown':
          const choices = q.choices.map(c => c.value);
          const questionCopy = currentPage.questions.find(q => q.name.endsWith('--question-copy') && q.getType() === 'radiogroup' || q.getType() === 'dropdown');

          correctAnswerIndex = choices.indexOf(correctAnswer);
          userAnswerIndex = choices.indexOf(userAnswer);
          isCorrect = userAnswer === correctAnswer;
          setTimeout(() => {
            const questionElement = document.getElementById(questionCopy.id);
            const choiceElements = questionElement.querySelectorAll('.nv-radio--item');
            if (correctAnswerIndex !== null) {
              choiceElements[correctAnswerIndex].classList.add('answer-item--correct');
            }
            if (userAnswerIndex !== null) {
              if (isCorrect) {
                choiceElements[userAnswerIndex].classList.add('answer-item--correct');
              } else {
                choiceElements[userAnswerIndex].classList.add('answer-item--incorrect');
              }
            }
          }, 200);
          break
        case 'text':
          isCorrect = userAnswer && userAnswer.toString().trim() === correctAnswer.toString().trim();
          break;
      }

      const classes = ['answer'];
      classes.push(isCorrect ? 'answer--correct' : 'answer--incorrect');
      currentPage.questions[currentPage.questions.length - 2].html = `<div class='${classes.join(' ')}'>The right answer was: ${correctLabels.join('; ')}</div>`;

      setTimeout(() => {
        const container = document.querySelector('.sd-body__page');
        const target = container?.querySelector('.explanation-title');

        if (container && target) {
          const containerRect = container.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();

          const offset = targetRect.top - containerRect.top + container.scrollTop;

          container.scrollTo({
            top: offset,
            behavior: 'smooth',
          });
        }
      }, 300);
    });
  }
}

export function buildExplanationPage(page, pageIndex) {
  const explanationPageElements = [];

  const explanationTitle = {
    'type': 'html',
    'name': `${page.name}-explanation-title`,
    'html': `<h2 class='h--smallest explanation-title'>${Config.I18n.Quiz.ExplanationTitle(pageIndex + 1)}</h2>`,
  };

  const answerStatus = {
    'type': 'html',
    'name': `${page.name}-answer-status`,
    'html': `<h3 data-question-index="${pageIndex}"></h3>`
  };

  const explanationElement = page.elements.pop();

  explanationElement.html = `<div class='explanation-html-wrapper'>${markdownToHtml(explanationElement.html)}</div>`;

  // Copy elements from prev page.
  for (const el of page.elements) {
    const explanationEl = {...el};
    explanationEl.name = `${explanationEl.name}--question-copy`;
    explanationEl.isRequired = false;
    explanationEl.readOnly = true;
    explanationPageElements.push(explanationEl);
  }

  explanationPageElements.push(explanationTitle);
  explanationPageElements.push(answerStatus);
  explanationPageElements.push(explanationElement);

  return {
    name: `${page.name}-explanation`,
    elements: explanationPageElements,
  }
}

export function buildSummaryPage(page, pageIndex) {
  const explanationPageElements = [];

  const explanationElement = page.elements.pop();

  explanationElement.html = `<div class='explanation-html-wrapper'>${markdownToHtml(explanationElement.html)}</div>`;

  explanationPageElements.push({
    'type': 'html',
    'name': `${page.name}-explanation-title`,
    'html':`<div class="text-bold summary-question-number">${Config.I18n.Quiz.ExplanationNumber(pageIndex + 1)}</div>`,
  });

  // Copy elements from prev page.
  for (const el of page.elements) {
    const pageQuestion = {...el};
    const {type, name} = pageQuestion;

    switch (type){
      case 'html':
        pageQuestion.name = `${pageQuestion.name}--question-text`;
        pageQuestion.isRequired = false;
        pageQuestion.readOnly = true;

        explanationPageElements.push(pageQuestion);
        break;
      default:
        explanationPageElements.push(pageQuestion);
        break;
    }

    const answerStatus = {
      'type': 'html',
      'name': `${page.name}-answer-status`,
      'html': `<div data-question-answer="${name}"></div>`
    };
    explanationPageElements.push(answerStatus);
  }

  explanationPageElements.push(explanationElement);

  return {
    name: `${page.name}-explanation`,
    elements: explanationPageElements,
  }
}
